import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import bannerImage from '../assets/images/Banner.jpeg';

export const Carousel = ({ slides, autoPlay = true, interval = 5000 }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (isPlaying && slides.length > 1) {
      const timer = setInterval(nextSlide, interval);
      return () => clearInterval(timer);
    }
  }, [isPlaying, interval, slides.length]);

  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden rounded-2xl">
      {/* Slides */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
              index === currentSlide ? 'translate-x-0' : 
              index < currentSlide ? '-translate-x-full' : 'translate-x-full'
            }`}
          >
            {/* Imagem de fundo */}
            <div 
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              {/* Overlay gradiente */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
              
              {/* Conteúdo do slide */}
              <div className="relative z-10 h-full flex items-center">
                <div className="container-custom">
                  <div className="max-w-2xl">
                    {slide.badge && (
                      <div className="inline-block bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                        {slide.badge}
                      </div>
                    )}
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                      {slide.title}
                    </h1>
                    
                    {slide.subtitle && (
                      <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                        {slide.subtitle}
                      </p>
                    )}
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      {slide.primaryButton && (
                        slide.primaryButton.href?.startsWith('http')
                          ? (
                            <a
                              href={slide.primaryButton.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-lg inline-flex items-center justify-center"
                            >
                              {slide.primaryButton.text}
                            </a>
                          ) : (
                            <Link
                              to={slide.primaryButton.href}
                              className="btn btn-primary btn-lg inline-flex items-center justify-center"
                            >
                              {slide.primaryButton.text}
                            </Link>
                          )
                      )}
                      
                      {slide.secondaryButton && (
                        slide.secondaryButton.href?.startsWith('http')
                          ? (
                            <a
                              href={slide.secondaryButton.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline btn-lg border-white text-white hover:bg-white hover:text-gray-900 inline-flex items-center justify-center"
                            >
                              {slide.secondaryButton.text}
                            </a>
                          ) : (
                            <Link
                              to={slide.secondaryButton.href}
                              className="btn btn-outline btn-lg border-white text-white hover:bg-white hover:text-gray-900 inline-flex items-center justify-center"
                            >
                              {slide.secondaryButton.text}
                            </Link>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controles de navegação */}
      {slides.length > 1 && (
        <>
          {/* Botões anterior/próximo */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-all duration-200"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-all duration-200"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Indicadores */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <div className="flex space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-white scale-125' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Controle de play/pause */}
          <button
            onClick={togglePlayPause}
            className="absolute top-6 right-6 p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-all duration-200"
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5" />
            )}
          </button>
        </>
      )}
    </div>
  );
};

// Componente de banner simples (sem carrossel)
export const Banner = ({ image, title, subtitle, buttons, badge }) => {
  return (
    <div className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden rounded-2xl">
      {/* Imagem de fundo */}
      <div 
        className="w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${image})` }}
      >
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
        
        {/* Conteúdo */}
        <div className="relative z-10 h-full flex items-center">
          <div className="container-custom">
            <div className="max-w-2xl">
              {badge && (
                <div className="inline-block bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  {badge}
                </div>
              )}
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {title}
              </h1>
              
              {subtitle && (
                <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                  {subtitle}
                </p>
              )}
              
              {buttons && (
                <div className="flex flex-col sm:flex-row gap-4">
                  {buttons.map((button, index) => (
                    <a
                      key={index}
                      href={button.href}
                      className={`btn btn-lg inline-flex items-center justify-center ${
                        button.primary 
                          ? 'btn-primary' 
                          : 'btn-outline border-white text-white hover:bg-white hover:text-gray-900'
                      }`}
                    >
                      {button.text}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
